import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { inject } from '@angular/core';
import { AuthGuardData, createAuthGuard } from 'keycloak-angular';

/**
 * Requires a Keycloak session — and deliberately nothing more.
 *
 * This guard used to demand the `manage-account` role out of the token's
 * resource roles. That was never a Tracer permission: `manage-account` is
 * a Keycloak built-in on the built-in `account` client, letting a user
 * administer their OWN Keycloak account. Tracer manages no Keycloak
 * users; it uses Keycloak purely as an OAuth identity provider, and the
 * string appears nowhere in the Tracer API.
 *
 * Nor was it the authorization boundary. The API decides what a caller
 * may see — common.IsAdmin, and the per-consumer/per-agency scoping
 * applied to every session and request query. A client-side role check
 * cannot enforce anything a browser cannot already bypass, so this guard
 * was gating the UI on a role whose only real property was that Keycloak
 * hands it out by default.
 *
 * Which is exactly how it failed. The role arrives incidentally, via the
 * realm's `default-roles-<realm>` composite, so in a healthy realm every
 * user passes and nobody discovers the check exists. When a realm-import
 * artifact left that composite carrying only `offline_access`, the
 * incidental role stopped being granted and a check that should never
 * have existed locked every route except home — silently, because its
 * denial target was not a registered route.
 *
 * So the requirement is now the honest one: be signed in. If Tracer ever
 * needs real role gating, it should be written against Tracer's own roles
 * and enforced in the API first, with this guard only mirroring it for
 * the sake of a decent error page.
 */
const isAccessAllowed = async (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData
): Promise<boolean | UrlTree> => {
  if (authData.authenticated) {
    return true;
  }

  const router = inject(Router);

  // Carries the refused URL so /forbidden can explain itself and offer to
  // resume, rather than dumping the caller on the home page.
  return router.createUrlTree(['/forbidden'], {
    queryParams: { reason: 'unauthenticated', from: state.url },
  });
};

export const canActivateAuthenticated = createAuthGuard<CanActivateFn>(isAccessAllowed);
